// src/lib/productStore.js
import storefront from '@/lib/storefront.js';
import * as queries from '@/lib/queries.js';
import { emitter } from '@/main.js';
import { handleize, buildProductTitleSearchQuery } from '@/lib/vue-helpers.js';
import { toRaw } from 'vue';

// How many failed opens before ensureOpen() stops retrying on read/write. A restore event
// resets the count.
const MAX_DB_OPEN_FAILURES = 3;

class ProductStore {
  constructor() {
    this.dbName = 'productVariantStore';
    this.storeName = 'productData';
    this.db = null;
    this.currentProductTitle = '';
    this.currentProductHandle = '';
    this.productsByTitleLookup = null;
    this.imageNS = null;
    this.imageKey = null;
    this.imageNS2 = null;
    this.imageKey2 = null;
    this.vimageNS = null;
    this.vimageKey = null;
    this.bundleSwatchMetafieldNameSpace = null;
    this.bundleSwatchMetafieldName = null;
    this.hasUpsellProduct = false;
    this.hasUpsellProduct2 = false;
    this.pendingFetches = {};
    this.collectionCache = new Map();
    this.collectionPendingFetches = {};
    this.dbDisabled = false;
    // bfcache lifecycle state: dbOpening guards against concurrent initDB() runs (pageshow
    // and resume can both fire on the same restore); bfcacheClosed means we closed on the
    // way out and have not seen the page come back, which is the one state nothing may
    // reopen in; dbGeneration identifies the newest open so a stale one cannot clobber
    // either; dbOpenFailures caps retries once IndexedDB is clearly unavailable.
    this.dbOpening = false;
    this.bfcacheClosed = false;
    this.dbGeneration = 0;
    this.dbOpenFailures = 0;
    this.warmInFlightKey = null;
    this.warmQueued = null;
    this.pendingMetafields = {};
    // Fail-open: if IndexedDB init fails, the store runs without cache.
    this.openInBackground();

    this.registerBfcacheHandlers();
  }

  // Close the IndexedDB connection before the page is frozen or enters the
  // back/forward cache: an open connection (or in-flight transaction) makes the
  // page ineligible for bfcache. Reopen on restore. Handlers swallow errors so a
  // throw can never evict the page from bfcache.
  registerBfcacheHandlers() {
    if (typeof window === 'undefined' || !window.addEventListener) return;
    const close = () => {
      try {
        this.closeConnection();
      } catch (err) {
        // Never throw during pagehide/freeze
      }
    };
    const reopen = () => {
      try {
        this.reopenConnection();
      } catch (err) {
        // Never throw during restore
      }
    };
    window.addEventListener('pagehide', close);
    window.addEventListener('pageshow', event => {
      if (event && event.persisted) reopen();
    });
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('freeze', close);
      document.addEventListener('resume', reopen);
      // Third, independent restore signal. pageshow(persisted) and resume are the precise
      // ones, but becoming visible again is the broadest evidence the page is genuinely
      // back, and it is what recovers us if either of those is missed. It also covers a
      // plain background-tab freeze, where only visibility changes.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') reopen();
      });
    }
  }

  closeConnection() {
    this.bfcacheClosed = true;
    // Supersede any open still in flight so its onsuccess closes its own connection
    // rather than installing one after we have decided to hold none. dbOpening has to be
    // cleared here too: a superseded open deliberately writes no shared state, so it will
    // not clear the flag itself, and everything that reopens short-circuits on it.
    this.dbGeneration += 1;
    this.dbOpening = false;
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  reopenConnection() {
    this.bfcacheClosed = false;
    // An explicit restore is reason enough to try again after earlier failures.
    this.dbOpenFailures = 0;
    if (this.dbDisabled || this.db || this.dbOpening) return;
    this.openInBackground();
  }

  openInBackground() {
    // Do not remove this catch — it converts rejections to resolved promises, which is
    // what lets every awaiting caller fall through to the no-cache path instead of throwing.
    // It must not touch this.db: a superseded open can reject long after a newer one
    // installed a live connection, and clearing the pointer here would drop that
    // connection without closing it - the orphan this change exists to remove. initDB does
    // that bookkeeping instead, where it can tell a real failure from a deliberate one.
    this.dbInitialized = this.initDB().catch(err => {
      console.warn('IndexedDB open failed, running without cache:', err);
    });
    return this.dbInitialized;
  }

  // Recovering the connection must not hinge on one event firing. this.db can be null for
  // reasons that have nothing to do with bfcache - the first open failed, a versionchange
  // closed us, an open was in flight when the page went away - and in all of those the
  // restore handlers never fire, so a read would return null for the rest of the page's
  // life and silently remove the cache this store exists for. Reads and writes come
  // through here so those cases heal themselves.
  async ensureOpen() {
    await this.dbInitialized;
    if (this.db || this.dbDisabled) return;
    // The one case we must not reopen in: closed for bfcache with no restore seen yet.
    // Opening here is the exact thing that evicts the page, and a read cannot tell us
    // whether the page is active - pagehide fires before the document is marked hidden,
    // so even visibilityState still reads 'visible' at that point. Coming back is an
    // event, not something to infer: pageshow, resume and visibilitychange all clear this.
    if (this.bfcacheClosed) return;
    // Cap retries: a genuinely unavailable IndexedDB would otherwise add the 3s open
    // timeout to every call. A restore clears this.
    if (this.dbOpenFailures >= MAX_DB_OPEN_FAILURES) return;
    if (!this.dbOpening) this.openInBackground();
    await this.dbInitialized;
  }

  isLoggingEnabled(speedTrackingEnabled = false) {
    return Boolean(window.productFamiliesDataDebug) || speedTrackingEnabled;
  }

  initDB() {
    // A close/reopen cycle can leave an earlier open still in flight. Stamp each attempt
    // so a superseded one cannot write to shared state - it would clear a live dbOpening
    // or overwrite this.db with a connection nobody closes, which is the orphaned blocker
    // this whole change exists to remove.
    const generation = ++this.dbGeneration;
    const isCurrent = () => generation === this.dbGeneration;
    this.dbOpening = true;
    return new Promise((resolve, reject) => {
      // Only a current open counts as a failure - one we superseded on purpose (a bfcache
      // close, or a newer open) must not push us toward the retry cap.
      const fail = error => {
        if (isCurrent()) {
          this.dbOpening = false;
          this.dbOpenFailures += 1;
          this.db = null;
        }
        reject(error);
      };
      const timeout = setTimeout(() => fail(new Error('IndexedDB open timed out')), 3000);
      let request;
      try {
        request = indexedDB.open(this.dbName, 1);
      } catch (error) {
        // open() throws synchronously where storage is denied outright - blocked cookies,
        // some private modes, embedded contexts. Reset here or dbOpening stays true and
        // every later reopen short-circuits on it for the life of the page.
        clearTimeout(timeout);
        fail(error);
        return;
      }

      request.onerror = () => {
        clearTimeout(timeout);
        fail(request.error);
      };
      request.onblocked = () => {
        clearTimeout(timeout);
        if (!isCurrent()) {
          resolve();
          return;
        }
        this.dbOpening = false;
        console.warn('IndexedDB blocked by another tab, running without cache');
        this.dbDisabled = true;
        this.db = null;
        resolve();
      };
      request.onsuccess = () => {
        clearTimeout(timeout);
        if (isCurrent()) this.dbOpening = false;
        // Hand back the connection unless this open has been superseded or another tab
        // blocked us. A superseded request still owns its connection, so it has to close
        // it here. bfcacheClosed is belt-and-braces: closeConnection() already supersedes
        // in-flight opens, so it should never be the deciding term.
        if (!isCurrent() || this.dbDisabled || this.bfcacheClosed) {
          try {
            request.result.close();
          } catch (err) {
            // ignore
          }
          if (isCurrent()) this.db = null;
        } else {
          const db = request.result;
          this.db = db;
          this.dbOpenFailures = 0;
          // Bind to the connection that fired, not to whatever this.db points at when it
          // fires: after a close/reopen cycle that is a different object, and between the
          // two it is null, where this.db.close() would throw inside an IndexedDB handler.
          db.onversionchange = () => {
            db.close();
            if (this.db === db) this.db = null;
          };
        }
        resolve();
      };

      request.onupgradeneeded = event => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'productTitle' });
        }
      };
    });
  }

  async getData(productTitle) {
    await this.ensureOpen();
    if (!this.db) return null;

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.get(productTitle);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      } catch (error) {
        reject(error);
      }
    });
  }

  metafieldFingerprint(vars = {}) {
    return [vars.imageNS, vars.imageKey, vars.imageNS2, vars.imageKey2, vars.vimageNS, vars.vimageKey, vars.bundleSwatchMetafieldNameSpace, vars.bundleSwatchMetafieldName].map(v => v || '').join('|');
  }

  metafieldScore(fingerprint) {
    if (!fingerprint || typeof fingerprint !== 'string') return 0;
    return fingerprint.split('|').filter(Boolean).length;
  }

  // True when every metafield the caller asked for was also requested, in the same slot, by the
  // fetch that produced `cached`. Equal fingerprints and richer ones pass; a fingerprint with the
  // same score but different fields does not, since the payload would still be missing a field.
  metafieldsCover(cached, requested) {
    const cachedParts = String(cached || '').split('|');
    const requestedParts = String(requested || '').split('|');
    return requestedParts.every((value, index) => !value || cachedParts[index] === value);
  }

  async setData(productTitle, data, metafields = '', { allowDowngrade = true } = {}) {
    await this.ensureOpen();
    if (!this.db) return;

    if (!allowDowngrade) {
      try {
        const existing = await this.getData(productTitle);
        if (existing?.data?.length && Date.now() - existing.timestamp < 5 * 60 * 1000 && this.metafieldScore(existing.metafields) >= this.metafieldScore(metafields)) {
          return;
        }
      } catch (dbErr) {
        console.warn('IndexedDB read failed before write:', dbErr);
      }
    }

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.put({ productTitle, data, timestamp: Date.now(), metafields });

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      } catch (error) {
        reject(error);
      }
    });
  }

  getPreloadedProductData(productTitle, productHandle = null, jsonElementId = 'product-families-json-data') {
    const matchProduct = product => {
      if (!product) return false;
      if (productHandle && product.handle === productHandle) return true;
      return handleize(product.title) === handleize(productTitle);
    };

    if (jsonElementId === 'product-families-json-data' && window.productFamiliesData && Array.isArray(window.productFamiliesData) && window.productFamiliesData.length > 0) {
      const matchingProducts = window.productFamiliesData.filter(matchProduct);

      if (matchingProducts.length > 0) {
        if (matchingProducts.some(product => this.isBundleProductType(product.productType))) {
          return null;
        }
        if (this.isLoggingEnabled()) {
          console.log(`Using pre-loaded PDP stitch products data for "${productTitle}" (${matchingProducts.length} products found)`);
        }
        return matchingProducts;
      }
    }

    const jsonElement = document.getElementById(jsonElementId);
    if (jsonElement) {
      try {
        const productFamiliesData = JSON.parse(jsonElement.textContent);
        if (productFamiliesData && Array.isArray(productFamiliesData) && productFamiliesData.length > 0) {
          const matchingProducts = productFamiliesData.filter(matchProduct);

          if (matchingProducts.length > 0) {
            if (matchingProducts.some(product => this.isBundleProductType(product.productType))) {
              return null;
            }
            if (this.isLoggingEnabled()) {
              console.log(`Using pre-loaded PDP stitch products data from JSON for "${productTitle}" (${matchingProducts.length} products found)`);
            }
            return this.processProductFamiliesData(matchingProducts);
          }
        }
      } catch (error) {
        console.warn('Error reading PDP stitch products JSON data:', error);
      }
    }

    return null;
  }

  getLiquidBootstrapProductData(productTitle, productHandle = null) {
    const preloadedData = this.getPreloadedProductData(productTitle, productHandle, 'mini-pdp-graphql-fallback-data');
    if (!preloadedData || !preloadedData.length) return [];
    return this.processProductFamiliesData(preloadedData);
  }

  async fetchGraphqlProductData(productTitle, countryCode, imageLookUpMax, productsByTitleLookup, metafieldVariables, productHandle = null) {
    let productData = [];

    if (productHandle) {
      const handleResponse = await storefront.request(queries.productVariantsByHandle, {
        variables: {
          handle: productHandle,
          countryIso2: countryCode,
          images: imageLookUpMax,
          ...metafieldVariables
        }
      });
      if (handleResponse?.errors?.length) {
        console.warn('Storefront handle lookup errors:', handleResponse.errors);
      }
      const handleProduct = handleResponse?.data?.product;
      if (handleProduct) {
        productData.push(handleProduct);
      }
    }

    const titleResponse = await storefront.request(queries.productVariantsByTitle, {
      variables: {
        query: buildProductTitleSearchQuery(productTitle),
        countryIso2: countryCode,
        images: imageLookUpMax,
        totalProduct: productsByTitleLookup,
        ...metafieldVariables
      }
    });
    if (titleResponse?.errors?.length) {
      console.warn('Storefront title lookup errors:', titleResponse.errors);
    }
    const titleProducts = titleResponse?.data?.products?.edges?.map(edge => edge.node) ?? [];
    titleProducts.forEach(product => {
      if (product && !productData.some(item => item.handle === product.handle)) {
        productData.push(product);
      }
    });

    return productData;
  }

  async fetchProductData(productTitle, countryCode, imageLookUpMax, productsByTitleLookup, imageNS, imageKey, imageNS2, imageKey2, vimageNS, vimageKey, bundleSwatchMetafieldNameSpace, bundleSwatchMetafieldName, productHandle = null, options = {}) {
    // Use a composite key so pricing is cached per-country
    const cacheKey = `${productTitle}::${countryCode}`;
    const silent = Boolean(options?.silent);
    const pendingKey = silent ? `${cacheKey}::silent` : cacheKey;
    const metafieldVariables = {
      imageNS: imageNS || '',
      imageKey: imageKey || '',
      imageNS2: imageNS2 || '',
      imageKey2: imageKey2 || '',
      vimageNS: vimageNS || '',
      vimageKey: vimageKey || '',
      bundleSwatchMetafieldNameSpace: bundleSwatchMetafieldNameSpace || '',
      bundleSwatchMetafieldName: bundleSwatchMetafieldName || ''
    };
    const metafields = this.metafieldFingerprint(metafieldVariables);
    const emit = (data, title, handle) => {
      if (!silent) this.processAndEmitData(data, title, handle);
    };
    if (this.pendingFetches[pendingKey]) return this.pendingFetches[pendingKey];
    // A warm and a real request use different pending keys, so the same product would
    // otherwise be fetched twice when a real request lands while the warm is still in
    // flight. Only join a warm that covers this caller's metafields - a thinner warm
    // cannot satisfy it. Emit here rather than flagging the warm, so the emission
    // cannot be missed if the warm is already past its own emit point.
    if (!silent) {
      const warmKey = `${cacheKey}::silent`;
      if (this.pendingFetches[warmKey] && this.metafieldsCover(this.pendingMetafields[warmKey], metafields)) {
        const warmPromise = this.pendingFetches[warmKey];
        this.pendingMetafields[pendingKey] = metafields;
        this.pendingFetches[pendingKey] = (async () => {
          try {
            const data = await warmPromise;
            this.processAndEmitData(data, productTitle, productHandle);
            return data;
          } finally {
            delete this.pendingFetches[pendingKey];
            delete this.pendingMetafields[pendingKey];
          }
        })();
        return this.pendingFetches[pendingKey];
      }
    }
    this.pendingMetafields[pendingKey] = metafields;
    this.pendingFetches[pendingKey] = (async () => {
      try {
        this.currentProductTitle = productTitle;
        this.currentProductHandle = productHandle || '';
        this.productsByTitleLookup = productsByTitleLookup;
        this.imageNS = imageNS || this.imageNS;
        this.imageKey = imageKey || this.imageKey;
        this.imageNS2 = imageNS2 || this.imageNS2;
        this.imageKey2 = imageKey2 || this.imageKey2;
        this.vimageNS = vimageNS || this.vimageNS;
        this.vimageKey = vimageKey || this.vimageKey;
        this.bundleSwatchMetafieldNameSpace = bundleSwatchMetafieldNameSpace || this.bundleSwatchMetafieldNameSpace;
        this.bundleSwatchMetafieldName = bundleSwatchMetafieldName || this.bundleSwatchMetafieldName;

        const preloadedData = this.getPreloadedProductData(productTitle, productHandle);
        if (preloadedData && preloadedData.length > 0) {
          // Use pre-loaded data instead of making GraphQL call
          // Note: getPreloadedProductData may return already-processed data from window,
          // or raw data from JSON that needs processing. processProductFamiliesData is idempotent.
          const processedData = this.processProductFamiliesData(preloadedData);
          await this.setData(cacheKey, processedData, metafields, { allowDowngrade: !silent });
          emit(processedData, productTitle, productHandle);

          // Check if speed tracking is enabled
          const jsonElement = document.getElementById('product-families-json-data');
          const speedTrackingEnabled = jsonElement?.dataset.speedTracking === 'true';
          const loggingEnabled = this.isLoggingEnabled(speedTrackingEnabled);

          if (loggingEnabled && speedTrackingEnabled) {
            console.log(`[Plytix Speed] 📍 Data Source: Plytix PDP Stitch Products Metafield (used by ProductStore)`);
            console.log(`[Plytix Speed] ✅ Skipped GraphQL API call for "${productTitle}" - using pre-loaded PDP stitch products data`);
          } else if (loggingEnabled) {
            console.log(`Skipped GraphQL API call for "${productTitle}" - using pre-loaded PDP stitch products data`);
          }

          return processedData;
        }

        let cachedData = null;
        try {
          cachedData = await this.getData(cacheKey);
        } catch (dbErr) {
          console.warn('IndexedDB read failed, fetching from API:', dbErr);
        }

        const cacheFresh = cachedData && cachedData.data?.length && Date.now() - cachedData.timestamp < 5 * 60 * 1000;
        if (cacheFresh) {
          // Only paint the cached payload when it covers the requested metafields. A thinner
          // entry - a warm that could not name the PDP's metafields, say - would paint without
          // upsell or bundle imagery and then be replaced by the refetch below, swapping gallery
          // images after first paint. Better to wait for the complete payload.
          if (silent || this.metafieldsCover(cachedData.metafields, metafields)) {
            emit(cachedData.data, productTitle, productHandle);
            return cachedData.data;
          }
        }

        // Check if speed tracking is enabled and log GraphQL call
        const jsonElement = document.getElementById('product-families-json-data');
        const speedTrackingEnabled = jsonElement?.dataset.speedTracking === 'true';
        const graphqlStartTime = speedTrackingEnabled ? performance.now() : null;

        if (this.isLoggingEnabled(speedTrackingEnabled)) {
          console.log(`[Plytix Speed] 📍 Data Source: GraphQL API`);
          console.log(`[Plytix Speed] 🔄 Making GraphQL API call for "${productTitle}" (no PDP stitch products data available)`);
        }

        let productData = await this.fetchGraphqlProductData(productTitle, countryCode, imageLookUpMax, productsByTitleLookup, metafieldVariables, productHandle);

        if (!productData.length) {
          productData = this.getLiquidBootstrapProductData(productTitle, productHandle);
          if (productData.length && this.isLoggingEnabled(speedTrackingEnabled)) {
            console.log(`[Plytix Speed] GraphQL returned 0 products, using Liquid bootstrap for "${productTitle}"`);
          }
        }

        if (this.isLoggingEnabled(speedTrackingEnabled) && graphqlStartTime !== null) {
          const graphqlTime = performance.now() - graphqlStartTime;
          console.log(`[Plytix Speed] ⏱️ GraphQL API call completed: ${graphqlTime.toFixed(2)}ms`);
          console.log(`[Plytix Speed] 📦 Products returned from GraphQL: ${productData.length}`);
        }

        if (speedTrackingEnabled) {
          window.productFamiliesDataSource = 'graphql-api';
        }

        await this.fetchRemainingVariants(productData, countryCode);

        if (productData.length) {
          try {
            await this.setData(cacheKey, productData, metafields, { allowDowngrade: !silent });
          } catch (dbErr) {
            console.warn('IndexedDB write failed:', dbErr);
          }
        } else {
          console.warn('Storefront API returned 0 products for:', productTitle, productHandle ? `(handle: ${productHandle})` : '');
        }

        emit(productData, productTitle, productHandle);
        return productData;
      } catch (error) {
        console.error('Error in fetchProductData:', error);
        const liquidFallback = this.getLiquidBootstrapProductData(productTitle, productHandle);
        if (liquidFallback.length) {
          emit(liquidFallback, productTitle, productHandle);
          return liquidFallback;
        }
        emit([], productTitle, productHandle);
        return [];
      } finally {
        delete this.pendingFetches[pendingKey];
        delete this.pendingMetafields[pendingKey];
      }
    })();
    return this.pendingFetches[pendingKey];
  }

  splitMetafield(value) {
    if (!value || typeof value !== 'string' || !value.includes('.')) return { ns: null, key: null };
    const [ns, key] = value.split('.');
    return { ns: ns || null, key: key || null };
  }

  // Variant-scoped metafields arrive as a GraphQL-shaped [{namespace, key, value}] list; the
  // consumers look them up by the `namespace.key` token the theme setting names, so flatten to
  // a map. Blank values are dropped so an empty variant reads the same as a missing one.
  //
  // Returns null when there was no list at all, which is not the same as a list that yielded
  // nothing: an empty map means this variant genuinely has no value, while null means no
  // pre-serialized data reached us and the consumer still has to go fetch it.
  normalizeVariantMetafields(entries) {
    if (!Array.isArray(entries)) return null;
    const map = {};
    entries.forEach(entry => {
      if (!entry || !entry.namespace || !entry.key) return;
      if (entry.value === null || entry.value === undefined || entry.value === '') return;
      map[`${entry.namespace}.${entry.key}`] = entry.value;
    });
    return map;
  }

  warmFromPlp({ productTitle, productHandle, countryCode } = {}) {
    if (!productTitle) return;
    const country = countryCode || window.Shopify?.country;
    if (!country) return;
    const preloaded = this.getPreloadedProductData(productTitle, productHandle);
    if (preloaded?.length) return;

    const key = `${productTitle}::${country}`;
    const payload = { key, productTitle, productHandle: productHandle || null, country };
    if (this.warmInFlightKey) {
      this.warmQueued = payload;
      return;
    }
    this.startWarm(payload);
  }

  cancelWarmIfNotStarted(cacheKey) {
    if (this.warmQueued?.key === cacheKey) this.warmQueued = null;
  }

  startWarm(payload) {
    this.warmInFlightKey = payload.key;
    // The PDP only reuses a warm that requested the same metafields, and the product-section
    // globals that name them exist only on the PDP itself. Fall back to the theme-level
    // mirrors of those settings so a warm started from a collection page can match.
    const globals = window.siteGlobals || {};
    const upsell1 = this.splitMetafield(window.product_upsell_options_option_product || globals.pdp_warm_upsell_option_product_1);
    const upsell2 = this.splitMetafield(window.product_upsell_options_option_product_2 || globals.pdp_warm_upsell_option_product_2);
    const variantImage = this.splitMetafield(globals.pdp_warm_variant_image_metafield);
    const bundleSwatch = this.splitMetafield(globals.pdp_warm_bundle_swatch_metafield);
    const root = document.getElementById('fs-collection-products');
    const imageLookUpMax = parseInt(window.siteGlobals?.pdp_max_images, 10) || parseInt(root?.getAttribute('data-pdp-max-images'), 10) || 5;
    const productsByTitleLookup = parseInt(window.siteGlobals?.pdp_productsByTitleLookup, 10) || 10;

    this.fetchProductData(payload.productTitle, payload.country, imageLookUpMax, productsByTitleLookup, upsell1.ns, upsell1.key, upsell2.ns, upsell2.key, variantImage.ns, variantImage.key, bundleSwatch.ns, bundleSwatch.key, payload.productHandle, { silent: true }).finally(() => {
      this.warmInFlightKey = null;
      const next = this.warmQueued;
      this.warmQueued = null;
      if (next && next.key !== payload.key) this.startWarm(next);
    });
  }

  processAndEmitData(productData, productTitle, productHandle = null) {
    const effectiveTitle = productTitle || this.currentProductTitle;
    const effectiveHandle = productHandle || this.currentProductHandle || null;
    const processedData = this.processVariantData(productData, effectiveTitle, effectiveHandle);
    const result = {
      variants: processedData.variants,
      colors: processedData.colors,
      sizes: toRaw(processedData.sizes),
      widths: toRaw(processedData.widths),
      hasUpsellProduct: processedData.hasUpsellProduct,
      optionOneName: processedData.optionOneName,
      optionTwoName: processedData.optionTwoName,
      optionThreeName: processedData.optionThreeName,
      productTitle: effectiveTitle,
      isBundleProduct: processedData.isBundleProduct,
      bundleOptionNames: processedData.bundleOptionNames,
      bundleComponents: processedData.bundleComponents,
      bundledProducts: processedData.bundledProducts,
      bundleFamilies: processedData.bundleFamilies || [],
      activeBundleHandle: processedData.activeBundleHandle || null
    };
    emitter.emit('product-data-updated', result);
  }

  /**
   * Process pre-loaded product families data from Liquid template
   * This avoids additional GraphQL API calls by using data rendered server-side
   * @param {Array} productFamiliesData - Array of product nodes matching GraphQL structure
   * @returns {Array} Processed product data ready for use
   */
  processProductFamiliesData(productFamiliesData) {
    if (!productFamiliesData || !Array.isArray(productFamiliesData)) {
      console.warn('processProductFamiliesData: Invalid or empty data provided');
      return [];
    }

    // The data from Liquid already matches the structure from GraphQL (array of nodes)
    // So we can process it the same way as fetchProductData
    // Store the raw data for reference
    const processedData = productFamiliesData.map(product => {
      // Ensure all required nested structures exist
      return {
        ...product,
        images: product.images || { edges: [] },
        media: product.media || { edges: [] },
        variants: product.variants || { edges: [] }
      };
    });

    // Only log in debug mode to avoid console spam
    if (window.productFamiliesDataDebug) {
      console.log('Plytix PDP stitch products data (from Liquid):', processedData);
    }
    return processedData;
  }

  processVariantData(productData, productTitle, productHandle = null) {
    const handleizedTitle = handleize(productTitle);
    let matchingNodes = productData.filter(node => node && handleizedTitle === handleize(node.title));
    if (!matchingNodes.length && productHandle) {
      matchingNodes = productData.filter(node => node && node.handle === productHandle);
    }
    const bundleNodes = matchingNodes.filter(node => this.isBundleProductType(node.productType));

    if (bundleNodes.length) {
      return this.processBundleProducts(bundleNodes);
    }

    const nodesToProcess = matchingNodes.length ? matchingNodes : productData;
    const colorSet = new Set();
    const colorTagPrefix = 'color-';
    let colorPrice = '';
    let colorGroupId = '';
    let optionOneName = '';
    let optionTwoName = '';
    let optionThreeName = '';

    const result = nodesToProcess.reduce(
      (acc, node) => {
        const titleMatches = handleizedTitle === handleize(node.title);
        const handleMatches = productHandle && node.handle === productHandle;
        if (!node || (!titleMatches && !handleMatches)) {
          return acc;
        }

        const { handle, id, descriptionHtml, tags, upsellProductOption, upsellProductOption2, productType } = node;
        const faqsAccordionHtml = Object.prototype.hasOwnProperty.call(node, 'faqsAccordionHtml') ? node.faqsAccordionHtml : '';
        const images = node.images?.edges || [];
        const media = node.media?.edges || [];
        const variants = node.variants?.edges || [];

        if (upsellProductOption?.value || upsellProductOption2?.value) {
          acc.hasUpsellProduct = true;
        }

        const pid = this.getProductMasterIDfromBase(id);

        for (const tag of tags) {
          if (tag.startsWith(colorTagPrefix)) {
            colorGroupId = tag.trim();
          }
        }

        const validVariants = variants.filter(e => e?.node);
        if (validVariants.length < variants.length) {
          console.warn(`Filtered ${variants.length - validVariants.length} malformed variant edge(s) for:`, productTitle);
        }
        const variantDataList = validVariants.map(({ node: variantNode }) => {
          const variantData = {
            handle,
            descriptionHtml,
            variantId: this.getVarinatIDfromBase(variantNode.id),
            masterId: this.getProductMasterIDfromBase(variantNode.product.id),
            availableForSale: variantNode.availableForSale,
            price: variantNode.price.amount,
            compareAtPrice: variantNode.compareAtPrice ? variantNode.compareAtPrice.amount : '',
            color: '',
            size: '',
            width: '',
            quantityAvailable: variantNode.quantityAvailable,
            image: variantNode.image?.url || '',
            sku: variantNode.sku,
            productType,
            upsellProductId: upsellProductOption,
            upsellProductId2: upsellProductOption2,
            sizeDetails: variantNode.sizeDetails ? variantNode.sizeDetails.value : '',
            upsellImage: variantNode.upsellImage ? variantNode.upsellImage.references.edges : '',
            variantMetafields: this.normalizeVariantMetafields(variantNode.variantMetafields),
            bundleSelections: []
          };
          colorPrice = variantNode.price.amount;
          const selectedOptions = variantNode.selectedOptions || [];
          selectedOptions.forEach((option, index) => {
            if (index === 0) {
              variantData.color = option.value;

              if (!colorSet.has(option.value)) {
                colorSet.add(option.value);
                acc.colors.push({
                  name: option.value,
                  handle,
                  pid,
                  images,
                  media,
                  tags,
                  upsellProductOption,
                  upsellProductOption2,
                  productType,
                  description: descriptionHtml,
                  faqsAccordionHtml,
                  price: colorPrice,
                  colorGroupId
                });
                optionOneName = option.name;
              }
            }

            if (index === 1) {
              variantData.size = option.value;
              if (!acc.sizes.includes(option.value)) {
                acc.sizes.push(option.value);
                optionTwoName = option.name;
              }
            }

            if (index === 2) {
              variantData.width = option.value;
              if (!acc.widths.includes(option.value)) {
                acc.widths.push(option.value);
                optionThreeName = option.name;
              }
            }
          });
          return variantData;
        });
        acc.variants.push(...variantDataList);
        return acc;
      },
      {
        variants: [],
        colors: [],
        sizes: [],
        widths: [],
        hasUpsellProduct: false
      }
    );

    return {
      variants: result.variants,
      colors: result.colors,
      sizes: result.sizes,
      widths: result.widths,
      hasUpsellProduct: result.hasUpsellProduct,
      isBundleProduct: false,
      bundleOptionNames: [],
      bundleComponents: {},
      bundledProducts: [],
      bundleFamilies: [],
      activeBundleHandle: null,
      optionOneName,
      optionTwoName,
      optionThreeName
    };
  }

  processBundleProducts(bundleNodes) {
    const processedProducts = bundleNodes.map(node => this.processSingleBundleNode(node)).filter(Boolean);
    const bundleSortedProducts = this.sortBundlesByAdminOrder(processedProducts);
    const targetHandle = typeof window !== 'undefined' && window.pdp_product_handle ? window.pdp_product_handle : null;
    const orderedProducts = targetHandle ? this.prioritizeBundledProductsByHandle(bundleSortedProducts, targetHandle) : bundleSortedProducts;
    const activeProduct = orderedProducts[0] || this.buildEmptyBundleResult();
    const bundleFamilies = this.groupBundledProducts(orderedProducts);

    return {
      variants: activeProduct.variants,
      colors: activeProduct.colors,
      sizes: activeProduct.sizes,
      widths: activeProduct.widths,
      hasUpsellProduct: activeProduct.hasUpsellProduct,
      optionOneName: activeProduct.optionOneName,
      optionTwoName: activeProduct.optionTwoName,
      optionThreeName: activeProduct.optionThreeName,
      bundleOptionNames: activeProduct.bundleOptionNames,
      bundleComponents: activeProduct.bundleComponents,
      bundledProducts: orderedProducts,
      bundleFamilies,
      activeBundleHandle: activeProduct.handle || null,
      isBundleProduct: true,
      BundledProductRefHandle: activeProduct.BundledProductRefHandle
    };
  }

  processSingleBundleNode(node) {
    if (!node) {
      return null;
    }

    const colorSet = new Set();
    const sizeSet = new Set();
    const widthSet = new Set();
    const bundleComponentsMap = {};
    const bundleOptionNamesSet = new Set();
    const colors = [];
    const variants = [];
    const colorTagPrefix = 'color-';
    let colorGroupId = '';
    let optionOneName = '';
    let optionTwoName = '';
    let optionThreeName = '';

    if (Array.isArray(node.tags)) {
      for (const tag of node.tags) {
        if (tag.startsWith(colorTagPrefix)) {
          colorGroupId = tag.trim();
        }
      }
    }

    const images = node?.images?.edges || [];
    const media = node?.media?.edges || [];
    const variantsEdges = Array.isArray(node?.variants?.edges) ? node.variants.edges : [];
    const pid = this.getProductMasterIDfromBase(node.id);
    const filteredVariants = variantsEdges.filter(edge => edge?.node);
    const variantsToProcess = filteredVariants.filter(edge => edge?.node?.components?.edges?.length) || [];
    const variantEdgesToUse = variantsToProcess.length ? variantsToProcess : filteredVariants;

    variantEdgesToUse.forEach(({ node: variantNode }) => {
      const normalizedComponents = this.normalizeBundleComponents(variantNode?.components?.edges || []);
      const bundleSelections = normalizedComponents;
      const colorLabel = this.buildBundleColorLabel(bundleSelections, variantNode?.selectedOptions);
      const sizeLabel = this.buildBundleComponentSizeLabel(bundleSelections, variantNode?.selectedOptions);
      const widthLabel = '';
      const childSwatchMap = this.buildBundleChildSwatchMap(bundleSelections, variantNode?.selectedOptions);

      const variantData = {
        handle: node.handle,
        descriptionHtml: node.descriptionHtml,
        variantId: this.getVarinatIDfromBase(variantNode.id),
        masterId: this.getProductMasterIDfromBase(variantNode.product.id),
        availableForSale: variantNode.availableForSale,
        price: variantNode.price.amount,
        compareAtPrice: variantNode.compareAtPrice ? variantNode.compareAtPrice.amount : '',
        color: colorLabel,
        size: sizeLabel,
        width: widthLabel,
        quantityAvailable: variantNode.quantityAvailable,
        image: variantNode.image?.url || '',
        sku: variantNode.sku,
        productType: node.productType,
        upsellProductId: node.upsellProductOption,
        upsellProductId2: node.upsellProductOption2,
        sizeDetails: variantNode.sizeDetails ? variantNode.sizeDetails.value : '',
        upsellImage: variantNode.upsellImage ? variantNode.upsellImage.references.edges : '',
        variantMetafields: this.normalizeVariantMetafields(variantNode.variantMetafields),
        bundleSelections,
        childSwatchMap
      };

      bundleComponentsMap[variantData.variantId] = normalizedComponents;

      if (!colorSet.has(colorLabel)) {
        colorSet.add(colorLabel);
        const primaryChildProductId = this.getPrimaryChildProductId(bundleSelections);
        colors.push({
          name: colorLabel,
          handle: node.handle,
          pid: primaryChildProductId || variantData.masterId || pid,
          images,
          media,
          tags: node.tags,
          upsellProductOption: node.upsellProductOption,
          upsellProductOption2: node.upsellProductOption2,
          productType: node.productType,
          description: node.descriptionHtml,
          faqsAccordionHtml: Object.prototype.hasOwnProperty.call(node, 'faqsAccordionHtml') ? node.faqsAccordionHtml : '',
          price: variantNode.price.amount,
          colorGroupId,
          bundleComponents: normalizedComponents,
          bundleSwatch: node.bundleSwatch?.value,
          bundleSwatchImageUrl: node.bundleSwatch?.reference?.image?.url
        });
        optionOneName = optionOneName || 'Color';
      }

      if (sizeLabel && !sizeSet.has(sizeLabel)) {
        sizeSet.add(sizeLabel);
        optionTwoName = optionTwoName || 'Bundle Options';
      }

      if (widthLabel && !widthSet.has(widthLabel)) {
        widthSet.add(widthLabel);
        optionThreeName = optionThreeName || 'Width';
      }

      if (Array.isArray(variantNode?.selectedOptions)) {
        variantNode.selectedOptions.forEach(option => {
          if (option?.name) {
            bundleOptionNamesSet.add(option.name);
          }
        });
      }

      variants.push(variantData);
    });

    const bundleOptionNames = Array.from(bundleOptionNamesSet);
    const hasUpsellProduct = Boolean(node.upsellProductOption?.value || node.upsellProductOption2?.value);
    const bundledProductRefHandle = variants[0]?.bundleSelections?.[0]?.productHandle || null;

    return {
      handle: node.handle,
      title: node.title,
      id: node.id,
      productType: node.productType,
      variants,
      colors,
      sizes: Array.from(sizeSet),
      widths: Array.from(widthSet),
      hasUpsellProduct,
      optionOneName,
      optionTwoName,
      optionThreeName,
      bundleOptionNames,
      bundleComponents: bundleComponentsMap,
      bundleChildSwatches: variants.reduce((acc, variant) => {
        if (variant?.variantId) {
          acc[variant.variantId] = variant.childSwatchMap || {};
        }
        return acc;
      }, {}),
      BundledProductRefHandle: bundledProductRefHandle,
      bundleSwatch: node.bundleSwatch?.value,
      bundleSwatchImageUrl: node.bundleSwatch?.reference?.image?.url,
      isBundleProduct: true
    };
  }

  normalizeBundleComponents(componentEdges) {
    if (!Array.isArray(componentEdges)) {
      return [];
    }

    return componentEdges
      .map(({ node }) => {
        if (!node?.productVariant) {
          return null;
        }

        const variant = node.productVariant;
        const selectedOptions = variant.selectedOptions || [];
        const colorOption = this.getFirstMatchingOption(selectedOptions, ['color', 'colour', 'shade']);
        const sizeOption = this.getFirstMatchingOption(selectedOptions, ['size', 'waist', 'inseam', 'length']);
        const optionValues = selectedOptions.reduce((map, option) => {
          if (option?.name) {
            map[option.name] = option.value || '';
          }
          return map;
        }, {});

        return {
          quantity: node.quantity ?? 0,
          productVariantId: this.getVarinatIDfromBase(variant.id || ''),
          productId: variant.product?.id ? this.getProductMasterIDfromBase(variant.product.id) : '',
          sku: variant.sku || '',
          productHandle: variant.product?.handle || '',
          productTitle: variant.product?.title || '',
          selectedOptions,
          optionValues,
          color: colorOption?.value || '',
          colorOptionName: colorOption?.name || '',
          size: sizeOption?.value || '',
          sizeOptionName: sizeOption?.name || '',
          swatchColor: colorOption?.value || '',
          image: variant.image?.url || ''
        };
      })
      .filter(Boolean);
  }

  getFirstMatchingOptionValue(selectedOptions, keywords) {
    const match = this.getFirstMatchingOption(selectedOptions, keywords);
    return match?.value || '';
  }

  getFirstMatchingOption(selectedOptions, keywords) {
    if (!Array.isArray(selectedOptions) || !keywords?.length) {
      return null;
    }

    const normalizedKeywords = keywords.map(keyword => keyword.toLowerCase());
    for (const option of selectedOptions) {
      if (!option?.name) {
        continue;
      }
      const optionName = option.name.toLowerCase();
      if (normalizedKeywords.some(keyword => optionName.includes(keyword))) {
        return option;
      }
    }
    return null;
  }

  buildBundleColorLabel(bundleSelections, fallbackSelectedOptions) {
    const colors = Array.isArray(bundleSelections) ? bundleSelections.map(selection => selection.color).filter(Boolean) : [];

    if (colors.length) {
      return colors.join(' + ');
    }

    if (Array.isArray(bundleSelections)) {
      const titles = bundleSelections.map(selection => selection.productTitle).filter(Boolean);
      if (titles.length) {
        return titles.join(' + ');
      }
    }

    if (Array.isArray(fallbackSelectedOptions) && fallbackSelectedOptions.length) {
      const fallbackColor = this.getFirstMatchingOptionValue(fallbackSelectedOptions, ['color', 'colour', 'shade']);
      if (fallbackColor) {
        return fallbackColor;
      }
      const fallbackValues = fallbackSelectedOptions.map(option => option?.value).filter(Boolean);
      if (fallbackValues.length) {
        return fallbackValues.join(' / ');
      }
    }

    return 'Bundle';
  }

  buildBundleChildSwatchMap(bundleSelections, fallbackSelectedOptions) {
    if (!Array.isArray(bundleSelections) || !bundleSelections.length) {
      return {};
    }
    const swatchMap = {};
    bundleSelections.forEach((selection, index) => {
      const key = selection.productHandle || selection.productTitle || `child-${index}`;
      const colorValue = selection.swatchColor || selection.color || this.getFirstMatchingOptionValue(selection.selectedOptions, ['color', 'colour', 'shade']) || '';
      swatchMap[key] = {
        color: colorValue,
        optionName: selection.colorOptionName || 'Color'
      };
    });
    return swatchMap;
  }

  buildBundleComponentSizeLabel(bundleSelections, fallbackSelectedOptions) {
    if (Array.isArray(bundleSelections) && bundleSelections.length) {
      return bundleSelections
        .map(selection => {
          const label = selection.productTitle || selection.productHandle || 'Item';
          const sizeValue = selection.size || this.getFirstMatchingOptionValue(selection.selectedOptions, ['size', 'waist', 'inseam', 'length']);
          return sizeValue ? `${label}: ${sizeValue}` : label;
        })
        .join(' / ');
    }

    if (Array.isArray(fallbackSelectedOptions) && fallbackSelectedOptions.length) {
      return this.buildBundleSelectionLabel(fallbackSelectedOptions);
    }

    return 'Bundle';
  }

  prioritizeBundledProductsByHandle(products, handle) {
    const index = products.findIndex(product => product?.handle === handle);
    if (index <= 0) {
      return products;
    }
    const cloned = products.slice();
    const [matchingProduct] = cloned.splice(index, 1);
    cloned.unshift(matchingProduct);
    return cloned;
  }

  sortBundlesByAdminOrder(products) {
    if (!Array.isArray(products) || !products.length) {
      return products;
    }
    const orderTokens = this.getBundleOrderTokens();
    if (!orderTokens.length) {
      return products;
    }
    return products.slice().sort((a, b) => {
      const valueA = this.getBundleComparisonValue(a);
      const valueB = this.getBundleComparisonValue(b);
      const indexA = this.getBundleOrderIndex(orderTokens, valueA);
      const indexB = this.getBundleOrderIndex(orderTokens, valueB);
      if (indexA === indexB) {
        return valueA.localeCompare(valueB);
      }
      return indexA - indexB;
    });
  }

  getBundleComparisonValue(product) {
    if (!product) {
      return '';
    }
    const colorName = product?.colors?.[0]?.name;
    if (colorName) {
      return colorName;
    }
    return product?.title || '';
  }

  getBundleOrderTokens() {
    if (typeof window === 'undefined' || !window.pdp_bundle_order) {
      return [];
    }
    const source = window.pdp_bundle_order;
    if (Array.isArray(source)) {
      return source
        .map(token => token?.toString().trim().toLowerCase())
        .filter(Boolean);
    }
    return source
      .toString()
      .split(',')
      .map(token => token.trim().toLowerCase())
      .filter(Boolean);
  }

  getBundleOrderIndex(orderTokens, rawValue) {
    if (!orderTokens.length || rawValue == null) {
      return Number.MAX_SAFE_INTEGER;
    }
    const normalized = rawValue.toString().trim().toLowerCase();
    if (!normalized.length) {
      return Number.MAX_SAFE_INTEGER;
    }
    const index = orderTokens.indexOf(normalized);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  }

  buildEmptyBundleResult() {
    return {
      variants: [],
      colors: [],
      sizes: [],
      widths: [],
      hasUpsellProduct: false,
      optionOneName: '',
      optionTwoName: '',
      optionThreeName: '',
      bundleOptionNames: [],
      bundleComponents: {},
      bundledProducts: [],
      bundleFamilies: [],
      activeBundleHandle: null,
      isBundleProduct: true
    };
  }

  groupBundledProducts(products) {
    const familyMap = new Map();

    products.forEach(product => {
      if (!product) return;
      const key = this.buildBundleFamilyKey(product.title || product.handle);
      if (!familyMap.has(key)) {
        familyMap.set(key, {
          title: product.title,
          handles: [],
          products: [],
          childHandleSet: new Set()
        });
      }
      const family = familyMap.get(key);
      family.handles.push(product.handle);
      family.products.push(product);
      const bundleComponents = product.bundleComponents || {};
      Object.values(bundleComponents).forEach(componentList => {
        if (!Array.isArray(componentList)) return;
        componentList.forEach(component => {
          if (component?.productHandle) {
            family.childHandleSet.add(component.productHandle);
          }
        });
      });
    });

    return Array.from(familyMap.values()).map(family => ({
      title: family.title,
      handles: family.handles,
      products: family.products,
      childHandles: Array.from(family.childHandleSet || [])
    }));
  }

  buildBundleFamilyKey(title) {
    if (!title) {
      return 'bundle-family-unknown';
    }
    return handleize(title);
  }

  getPrimaryChildProductId(bundleSelections) {
    if (!Array.isArray(bundleSelections) || !bundleSelections.length) {
      return '';
    }
    const childWithId = bundleSelections.find(selection => selection?.productId);
    return childWithId?.productId || '';
  }

  isBundleProductType(productType) {
    if (!productType || typeof productType !== 'string') {
      return false;
    }
    return productType.trim().toLowerCase() === 'bundles';
  }

  buildBundleSelectionLabel(selections) {
    if (!Array.isArray(selections) || !selections.length) {
      return 'Bundle';
    }
    return selections
      .map(selection => selection?.value)
      .filter(Boolean)
      .join(' / ');
  }

  getVarinatIDfromBase(str) {
    return str.replace('gid://shopify/ProductVariant/', '');
  }

  getProductMasterIDfromBase(str) {
    return str.replace('gid://shopify/Product/', '');
  }

  async fetchCollectionUrl(collectionGid) {
    if (!collectionGid) return '';
    const cached = this.collectionCache.get(collectionGid);
    if (cached) return cached;
    if (this.collectionPendingFetches[collectionGid]) return this.collectionPendingFetches[collectionGid];
    this.collectionPendingFetches[collectionGid] = storefront
      .request(queries.getCollectionByGid, { variables: { id: collectionGid } })
      .then(response => {
        const handle = response.data?.collection?.handle;
        const url = handle ? '/collections/' + handle : '';
        if (url) this.collectionCache.set(collectionGid, url);
        return url;
      })
      .catch(() => '')
      .finally(() => delete this.collectionPendingFetches[collectionGid]);
    return this.collectionPendingFetches[collectionGid];
  }

  async fetchRemainingVariants(productData, countryCode) {
    const productsNeedingMore = productData.filter(node => node?.variants?.pageInfo?.hasNextPage);
    if (!productsNeedingMore.length) return;

    await Promise.all(productsNeedingMore.map(async (node) => {
      let hasNextPage = true;
      let after = node.variants.pageInfo.endCursor;

      while (hasNextPage) {
        try {
          const response = await storefront.request(queries.moreProductVariants, {
            variables: {
              productId: node.id,
              after,
              countryIso2: countryCode,
              vimageNS: this.vimageNS || '',
              vimageKey: this.vimageKey || ''
            }
          });

          const variantConnection = response?.data?.node?.variants;
          if (!variantConnection?.edges?.length) break;

          node.variants.edges.push(...variantConnection.edges);
          hasNextPage = variantConnection.pageInfo?.hasNextPage || false;
          after = variantConnection.pageInfo?.endCursor;
        } catch (err) {
          console.warn('Failed to fetch additional variants for product:', node.id, err);
          break;
        }
      }
    }));
  }
}

export const productStore = new ProductStore();
